import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import DismissibleIntro from "@/components/DismissibleIntro";
import ReopenGuidesBtn from "@/components/ReopenGuidesBtn";
import {
  Phone, WhatsappLogo, EnvelopeSimple, FilePdf, House,
  QrCode, Globe, Clock, CheckCircle,
} from "@phosphor-icons/react";

/**
 * RichiesteStima · pagina centralizzata "Richieste di valutazione"
 *
 * Mostra TUTTE le richieste di stima arrivate all'agente, sia da:
 *   • Volantino agente con QR (`source: "pronto_stima"`)
 *   • Landing pubblica `/quanto-vale-casa-mia` (`source: "quanto_vale_casa_mia"`)
 *
 * Filtra `kind === "valuation_request"` lato client (le richieste classiche
 * dalla pagina pubblica annuncio hanno kind diverso e restano nella loro
 * tab dedicata).
 *
 * Azioni rapide per ogni richiesta: telefono, WhatsApp, email, e —
 * preparatorio per step 2 — bottone "Genera PDF Stima Comparativa".
 */
export default function RichiesteStima() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | new | called | meeting | mandate
  const agentName = (user?.name || user?.full_name || "").trim() || null;

  useEffect(() => {
    api
      .get("/leads")
      .then((r) => {
        const all = Array.isArray(r.data) ? r.data : r.data?.leads || [];
        const valuations = all.filter((l) => l.kind === "valuation_request");
        setLeads(valuations);
      })
      .catch(() => toast.error("Impossibile caricare le richieste"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = leads.filter((l) => {
    if (filter === "all") return true;
    return (l.status || "new") === filter;
  });

  const updateStatus = async (leadId, newStatus) => {
    try {
      await api.patch(`/leads/${leadId}`, { status: newStatus });
      setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)));
      toast.success("Stato aggiornato");
    } catch (err) {
      toast.error("Errore aggiornamento");
    }
  };

  return (
    <AppShell title="Richieste stima" breadcrumb="Richieste stima">
      <div className="max-w-4xl mx-auto" data-testid="richieste-stima-page">
        {/* iter v28 r368 — User M1278: ogni descrizione è dismissibile.
            Pulsante "?" per riaprire le guide di questa sezione. */}
        <div className="flex items-center justify-end mb-1">
          <ReopenGuidesBtn
            keys={["richieste:intro", "richieste:come-arrivano"]}
            testId="richieste-reopen-guides"
          />
        </div>
        <DismissibleIntro storageKey="richieste:intro">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <h1 className="font-editorial text-[28px] sm:text-[34px] leading-tight text-[#1C1C1A]">
                Richieste di valutazione
              </h1>
              <p className="mt-1.5 text-[13.5px] text-[#6B6A65] leading-snug max-w-2xl">
                Sono i <strong className="text-[#1C1C1A]">proprietari di casa</strong> che vogliono <strong className="text-[#1C1C1A]">vendere</strong> e hanno chiesto a te una stima.
              </p>
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#5A6A4D] font-semibold">
              {leads.length} {leads.length === 1 ? "richiesta" : "richieste"}
            </div>
          </div>
        </DismissibleIntro>

        {/* iter 313 r12 — Pannello "Come arrivano qui?": chiarisce cosa NON è
            ovvio. L'utente: "non mi è chiaro a cosa servano". Mostro le 3 fonti
            con icone + 1 frase ciascuna + CTA per attivare/condividere.
            iter v28 r368 — wrappato in DismissibleIntro per permettere
            all'utente di nasconderlo. */}
        <DismissibleIntro storageKey="richieste:come-arrivano">
          <details className="mt-4 group" data-testid="richieste-stima-help">
            <summary className="cursor-pointer text-[12px] text-[#7C8B6E] hover:text-[#1C1C1A] font-semibold inline-flex items-center gap-1.5 list-none [&::-webkit-details-marker]:hidden">
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#7C8B6E]/15 text-[#7C8B6E] text-[10px] font-bold group-open:bg-[#7C8B6E] group-open:text-white transition-colors">?</span>
              Come fanno i proprietari ad arrivare qui?
            </summary>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <Link to="/dashboard/landing" className="bg-white border border-[#EAE7DF] hover:border-[#7C8B6E] rounded-xl p-3 transition-colors">
                <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#5A6A4D]">
                  <QrCode size={12} weight="fill" /> Volantino QR
                </div>
                <div className="mt-1.5 text-[12.5px] text-[#1C1C1A] font-medium">Lo lasci in posta, in bar, sotto i palazzi</div>
                <div className="mt-0.5 text-[11px] text-[#6B6A65]">Scansiona il QR → arriva sulla tua pagina stima → fa la stima → tu ricevi la richiesta qui</div>
              </Link>
              <Link to="/dashboard/landing" className="bg-white border border-[#EAE7DF] hover:border-[#7C8B6E] rounded-xl p-3 transition-colors">
                <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#5A6A4D]">
                  <Globe size={12} weight="fill" /> La tua pagina stima
                </div>
                <div className="mt-1.5 text-[12.5px] text-[#1C1C1A] font-medium">Il link che metti su WhatsApp · bio Instagram</div>
                <div className="mt-0.5 text-[11px] text-[#6B6A65]">Pagina pubblica col tuo logo · stima OMI · proprietario lascia i contatti</div>
              </Link>
              <div className="bg-white border border-[#EAE7DF] rounded-xl p-3">
                <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#5A6A4D]">
                  <Globe size={12} weight="fill" /> Landing pubblica
                </div>
                <div className="mt-1.5 text-[12.5px] text-[#1C1C1A] font-medium">&ldquo;Quanto vale casa mia&rdquo; su prontoimmo.it</div>
                <div className="mt-0.5 text-[11px] text-[#6B6A65]">SEO city pages · proprietari della tua zona ti vengono assegnati</div>
              </div>
            </div>
          </details>
        </DismissibleIntro>

        {/* Filtri stato */}
        <div className="mt-5 flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
          {[
            { v: "all", l: "Tutte", c: leads.length },
            { v: "new", l: "Da chiamare", c: leads.filter((l) => (l.status || "new") === "new").length },
            { v: "called", l: "Chiamate", c: leads.filter((l) => l.status === "called").length },
            { v: "meeting", l: "Appuntamento", c: leads.filter((l) => l.status === "meeting").length },
            { v: "mandate", l: "Mandato", c: leads.filter((l) => l.status === "mandate").length },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-medium border whitespace-nowrap transition-all ${
                filter === f.v
                  ? "bg-[#1C1C1A] border-[#1C1C1A] text-white"
                  : "bg-white border-[#EAE7DF] text-[#3F3D38] hover:border-[#7C8B6E]"
              }`}
              data-testid={`filter-${f.v}`}
            >
              {f.l} <span className="opacity-60 ml-0.5">·</span> <span>{f.c}</span>
            </button>
          ))}
        </div>

        {loading && (
          <div className="mt-12 text-center">
            <div className="inline-block w-8 h-8 rounded-full border-3 border-[#7C8B6E] border-t-transparent animate-spin" />
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="mt-12 bg-white border border-[#EAE7DF] rounded-2xl p-8 text-center">
            <House size={36} weight="duotone" className="text-[#C8C5BD] mx-auto" />
            <h3 className="font-editorial text-[19px] mt-3 text-[#1C1C1A]">
              {leads.length === 0 ? "Nessuna richiesta ancora" : "Niente in questa fase"}
            </h3>
            <p className="mt-2 text-[13px] text-[#6B6A65] max-w-md mx-auto leading-snug">
              {leads.length === 0
                ? "Quando un proprietario fa una stima dal tuo volantino QR o dalla landing pubblica Pronto, la richiesta apparirà qui."
                : "Cambia filtro per vedere le altre richieste."}
            </p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="mt-6 space-y-3">
            {filtered.map((lead) => (
              <LeadRow key={lead.id} lead={lead} onStatus={updateStatus} agentName={agentName} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function buildWaMessage(lead, agentName) {
  // iter v31 r25 — template WhatsApp smart: saluto orario + dati casa
  // + range stima leggibile + valore proposto + firma agente. Honest,
  // no claim falsi tipo "3 acquirenti pronti".
  const firstName = (lead.name || "").split(" ")[0] || "ciao";
  const input = lead.stima_data?.input || {};
  const result = lead.stima_data?.result || {};
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";

  const propertyBits = [];
  if (input.surface_sqm) propertyBits.push(`${input.surface_sqm} m²`);
  if (input.rooms) propertyBits.push(`${input.rooms} locali`);
  if (input.state) propertyBits.push(input.state);
  const propertyDesc = propertyBits.length ? ` (${propertyBits.join(", ")})` : "";

  const range =
    result.range_min && result.range_max
      ? `${Math.round(result.range_min / 1000)}k – ${Math.round(result.range_max / 1000)}k €`
      : "";

  const lines = [
    `${greeting} ${firstName},`,
    `ho ricevuto la tua richiesta di stima per ${input.city || "la tua casa"}${input.address ? `, ${input.address}` : ""}${propertyDesc}.`,
  ];
  if (range) {
    lines.push(`Dalla tua simulazione la fascia indicativa è ${range}.`);
  }
  lines.push("");
  lines.push("Per arrivare al valore preciso bastano 10 minuti — telefono o di persona, gratis.");
  lines.push("Quando ti torna più comodo: oggi pomeriggio, domani mattina, o un altro orario?");
  if (agentName) {
    lines.push("");
    lines.push(`— ${agentName}`);
  }
  return encodeURIComponent(lines.join("\n"));
}

function LeadRow({ lead, onStatus, agentName }) {
  const navigate = useNavigate();
  // iter v31 r25 — Snapshot stabile di "now" per il flag isFresh.
  // Senza questo, eslint react-hooks/purity blocca Date.now() in render.
  const [nowSnapshot] = useState(() => Date.now());
  const result = lead.stima_data?.result || {};
  const input = lead.stima_data?.input || {};
  const min = result.range_min ? `€${Math.round(result.range_min * 0.95).toLocaleString("it-IT")}` : "—";
  const max = result.range_max ? `€${Math.round(result.range_max * 1.05).toLocaleString("it-IT")}` : "—";
  const created = lead.created_at ? new Date(lead.created_at) : null;
  const ago = created ? timeAgo(created) : "—";

  const cleanPhone = (lead.phone || "").replace(/[^+\d]/g, "");
  const waText = buildWaMessage(lead, agentName);
  const isFresh = created && nowSnapshot - created.getTime() < 30 * 60 * 1000;
  const sourceLabel = lead.source === "quanto_vale_casa_mia"
    ? { icon: Globe, label: "Landing Pronto", color: "#7C8B6E" }
    : { icon: QrCode, label: "Volantino QR", color: "#5A6A4D" };
  const SourceIcon = sourceLabel.icon;

  return (
    <div className={`bg-white border rounded-xl p-4 sm:p-5 transition-all ${
      isFresh
        ? "border-[#25D366]/40 ring-1 ring-[#25D366]/20 shadow-[0_8px_30px_-12px_rgba(37,211,102,0.25)]"
        : "border-[#EAE7DF] hover:border-[#7C8B6E]"
    }`} data-testid={`lead-${lead.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {isFresh && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-bold bg-[#25D366] text-white animate-pulse"
                data-testid={`lead-fresh-${lead.id}`}
              >
                <WhatsappLogo size={11} weight="fill" /> Rispondi ora
              </span>
            )}
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold"
              style={{ backgroundColor: `${sourceLabel.color}15`, color: sourceLabel.color }}
            >
              <SourceIcon size={10} weight="fill" /> {sourceLabel.label}
            </span>
            <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.14em] text-[#9A968B] font-medium">
              <Clock size={10} weight="fill" /> {ago}
            </span>
            <StatusBadge status={lead.status || "new"} />
          </div>
          <div className="font-editorial text-[20px] text-[#1C1C1A] leading-tight">{lead.name}</div>
          <div className="mt-1 text-[12.5px] text-[#6B6A65] leading-snug">
            <strong>{input.city || "—"}</strong>
            {input.address ? ` · ${input.address}` : ""}
            {" · "}
            {input.surface_sqm ? `${input.surface_sqm} m²` : ""}
            {input.rooms ? ` · ${input.rooms} locali` : ""}
            {input.state ? ` · stato ${input.state}` : ""}
          </div>
          {lead.message && (
            <div className="mt-2 text-[12.5px] text-[#3F3D38] italic leading-snug bg-[#FAFAF7] p-2 rounded-md border border-[#EAE7DF]">
              &ldquo;{lead.message}&rdquo;
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-0.5">Range AVM</div>
          <div className="font-editorial text-[16px] text-[#1C1C1A] tabular-nums leading-tight">
            {min}<br/><span className="text-[#6B6A65]">– {max}</span>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {/* iter v31 r25 — WhatsApp primo e dominante, è il canale che converte
            quando il lead è ancora caldo. Messaggio pre-compilato smart con
            città, mq, range stima e firma agente (vedi buildWaMessage). */}
        <a
          href={`https://wa.me/${cleanPhone.replace(/^\+/, "")}?text=${waText}`}
          target="_blank" rel="noopener noreferrer"
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-white text-[13px] font-bold transition-all active:scale-[0.98] ${
            isFresh
              ? "bg-[#25D366] hover:bg-[#1FBC58] shadow-[0_10px_30px_-12px_rgba(37,211,102,0.6)]"
              : "bg-[#25D366] hover:bg-[#1FBC58]"
          }`}
          data-testid={`lead-wa-${lead.id}`}
        >
          <WhatsappLogo size={15} weight="fill" /> Apri WhatsApp con messaggio
        </a>
        <a
          href={`tel:${cleanPhone}`}
          className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md bg-white border border-[#EAE7DF] hover:border-[#7C8B6E] text-[#3F3D38] text-[12.5px] font-semibold transition-colors"
          data-testid={`lead-call-${lead.id}`}
        >
          <Phone size={13} weight="fill" /> Chiama
        </a>
        {lead.email && (
          <a
            href={`mailto:${lead.email}?subject=Stima ${input.city}&body=Ciao ${lead.name.split(" ")[0]},`}
            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md bg-white border border-[#EAE7DF] hover:border-[#7C8B6E] text-[#3F3D38] text-[12.5px] font-semibold transition-colors"
            data-testid={`lead-mail-${lead.id}`}
          >
            <EnvelopeSimple size={13} weight="fill" /> Email
          </a>
        )}
        <button
          onClick={() => navigate(`/dashboard/stima-comparativa?lead=${lead.id}`)}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md bg-[#1C1C1A] hover:bg-black text-white text-[12.5px] font-semibold transition-colors"
          data-testid={`lead-pdf-${lead.id}`}
        >
          <FilePdf size={13} weight="fill" /> PDF Stima Comparativa
        </button>
      </div>

      {/* Stato switcher */}
      <div className="mt-3 pt-3 border-t border-[#EAE7DF] flex items-center gap-1.5 flex-wrap">
        <span className="text-[10.5px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mr-1">Stato:</span>
        {[
          { v: "new", l: "Da chiamare" },
          { v: "called", l: "Chiamato" },
          { v: "meeting", l: "Appuntamento" },
          { v: "mandate", l: "Mandato" },
          { v: "closed", l: "Chiuso" },
        ].map((s) => (
          <button
            key={s.v}
            onClick={() => onStatus(lead.id, s.v)}
            className={`px-2 py-1 rounded-md text-[11px] font-medium transition-all ${
              (lead.status || "new") === s.v
                ? "bg-[#1C1C1A] text-white"
                : "bg-[#FAFAF7] border border-[#EAE7DF] text-[#6B6A65] hover:border-[#7C8B6E]"
            }`}
            data-testid={`lead-status-${lead.id}-${s.v}`}
          >
            {s.l}
          </button>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: { l: "Nuova", c: "#E11D48" },
    called: { l: "Chiamato", c: "#7C8B6E" },
    meeting: { l: "Appuntamento", c: "#D97706" },
    mandate: { l: "Mandato", c: "#059669" },
    closed: { l: "Chiuso", c: "#6B6A65" },
  };
  const v = map[status] || map.new;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold"
      style={{ backgroundColor: `${v.c}15`, color: v.c }}
    >
      <CheckCircle size={10} weight="fill" /> {v.l}
    </span>
  );
}

function timeAgo(d) {
  const ms = Date.now() - d.getTime();
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m} min fa`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h fa`;
  const days = Math.round(h / 24);
  if (days < 7) return `${days}g fa`;
  return d.toLocaleDateString("it-IT");
}
